/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useCallback, useLayoutEffect } from 'react';
import { Fade } from '@material-ui/core';

import i18n from 'i18next';

import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useSnackbar } from 'notistack';

import { PageContainer, Search } from '../../../app/styles';

import { IProfilePermission, ItemList } from '../../../utils/interfaces';

import FixedHeader from '../../../components/Tools/Breadcrumb';
import SearchBox from '../../../components/Tools/Search';

import LoaderPda from '../../../components/Tools/LoaderPda';
import ProfilePermissionsTable from './components/ProfilePermissionsTable';

import {
  selectOptionsRequest,
  searchBoxRequest,
} from './api/profilePermissions.api';

import { IProfileManupuled } from './types';

const ProfilePermissions: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);
  const [isShowingTable, setIsShowingTable] = useState(false);

  const [systems, setSystems] = useState<ItemList[]>([]);
  const [profilePermissions, setProfilePermissions] = useState<
    IProfilePermission[]
  >([]);
  const [profilePermissionsManipuled, setProfilePermissionsManipuledt] =
    useState<IProfileManupuled[]>([]);

  const traductorMsg = (msg: string) => {
    return i18n.t(msg);
  };

  const { profileCode } = useParams<Record<string, string | undefined>>();
  const codigoPerfil = String(profileCode);

  const clientId = Number(localStorage.getItem('@pdamodules::codigoCliente'));

  const getSystems = useCallback(async () => {
    setIsLoading(true);

    const systemsOptions = await selectOptionsRequest(clientId, codigoPerfil);

    setSystems(systemsOptions);
    setIsLoading(false);
  }, [clientId, codigoPerfil]);

  const groupPermissions = (permissions: IProfilePermission[]) => {
    const grouped: Record<string, IProfileManupuled[]> = {};
    permissions.forEach(p => {
      const parentGroup = permissions.find(
        x => x.codigoGrupo === p.codigoGrupo && !x.codigoMenuPai
      );
      const parentDescription = parentGroup?.descricaoMenu ?? '';

      if (!grouped[p.codigoGrupo]) {
        grouped[p.codigoGrupo] = [];
      }
      grouped[p.codigoGrupo].push({
        ...p,
        descricaoMenuPai: parentDescription,
        codigoPai: String(parentGroup?.codigoSistemaMenu),
      });
    });

    const orderedList: IProfileManupuled[] = [];
    Object.values(grouped).forEach(group => {
      const parent = group.find(item => !item.codigoMenuPai);
      if (parent) {
        orderedList.push(parent);
        const children = group.filter(item => item.codigoMenuPai);
        orderedList.push(...children);
      }
    });

    return orderedList;
  };

  const transformeProfiles = (profiles: IProfilePermission[]) => {
    const newData = groupPermissions(profiles);
    setProfilePermissionsManipuledt(newData);
  };

  const handleSearchBoxSubmit = async (data: any) => {
    if (data.systems === '') {
      enqueueSnackbar(traductorMsg('screens.profile.msgNoProfile'), {
        variant: 'warning',
      });

      return;
    }

    setIsShowingTable(false);
    setIsLoading(true);

    await searchBoxRequest(clientId, codigoPerfil, data)
      .then(res => {
        if (!res.data.length) {
          setProfilePermissions([]);
          enqueueSnackbar(traductorMsg('screens.profile.noRecordes'), {
            variant: 'warning',
          });
          return;
        }

        setProfilePermissions(res.data);
        transformeProfiles(res.data);
        setIsShowingTable(true);
      })
      .catch(err => {
        setIsShowingTable(false);
        enqueueSnackbar(
          `${traductorMsg('screens.user.error')}: ${err.message}`,
          {
            variant: 'error',
          }
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useLayoutEffect(() => {
    getSystems();
  }, [getSystems]);

  return (
    <>
      <FixedHeader
        title={t('screens.profile.title')}
        subTitle={String(t('screens.profileTable.permissions'))}
      />
      <Fade in timeout={1000} unmountOnExit>
        <PageContainer>
          <Search>
            <SearchBox
              handleSubmit={data => handleSearchBoxSubmit(data)}
              inputs={[
                {
                  name: 'systems',
                  label: String(t('screens.profile.titleEdit')),
                  type: 'select',
                  options: systems,
                  isRequired: true,
                  disabled: isLoading,
                  placeholder: String(t('screens.profile.titleEdit')),
                  xl: 12,
                  lg: 12,
                  xs: 12,
                  md: 12,
                  sm: 12,
                },
              ]}
              searchDisabled={isLoading}
            />
          </Search>
          {isLoading && <LoaderPda />}
        </PageContainer>
      </Fade>
      {!isLoading && isShowingTable && (
        <ProfilePermissionsTable
          rows={profilePermissions}
          profilePermissionsManipuled={profilePermissionsManipuled}
        />
      )}
    </>
  );
};

export default ProfilePermissions;
